/**
 * Attachments on a record — decision 54.
 *
 * One component for both places that file documents, because a shareholder's
 * firmaattest and an issuance's tegningsliste are the same interaction and the
 * only thing that differs is the sentence shown when nothing is filed yet.
 *
 * ## The empty state names the documents
 *
 * Decision 23 asks an empty state to name the cause rather than the absence,
 * and here the useful form of that is naming *what belongs there*. "No
 * attachments" tells a reader nothing; listing firmaattest, the signed
 * subscription form, the transfer agreement and the board consent tells them
 * what the register is missing and what to go and find.
 *
 * ## Filed, not uploaded
 *
 * Each row reads "142 KB · filed 14.08.2026". The date a document was filed is
 * the fact a share register is read for — when the firmaattest arrived, not
 * that one exists — so it sits on the row rather than in a tooltip.
 */
import { useState } from 'react'
import { fmtFileSize } from '@/shared/util/files'
import { formatDate } from '@/shared/util/dates'
import {
  uploadAttachment,
  deleteAttachment,
  attachmentUrl,
  type AttachmentScope,
} from './repository/attachmentsStorage'
import type { Attachment } from '@/types/ownership'
import './AttachmentsField.css'

const EMPTY: Record<AttachmentScope, string> = {
  shareholders:
    'Nothing filed on this shareholder yet — firmaattest, signed subscription form, transfer agreement, board consent.',
  issuances:
    'Nothing filed on this increase yet — signed protokoll, tegningsliste, Foretaksregisteret confirmation.',
}

export function AttachmentsField({
  scope,
  recordId,
  attachments,
  onChange,
}: {
  scope: AttachmentScope
  /** Undefined for a record that has never been saved. */
  recordId?: string
  attachments: Attachment[]
  onChange: (next: Attachment[]) => void
}) {
  const [busy, setBusy] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const pick = async (files: FileList | null) => {
    const chosen = Array.from(files ?? [])
    if (chosen.length === 0) return
    setError(null)
    setBusy((n) => n + chosen.length)

    // One at a time rather than Promise.all: a share register files two or
    // three documents, not thirty, and serial uploads mean a failure names the
    // file it happened on instead of collapsing into one rejected batch.
    const filed: Attachment[] = []
    for (const file of chosen) {
      try {
        filed.push(await uploadAttachment(scope, recordId, file))
      } catch (err) {
        setError(
          `${file.name} could not be filed: ${err instanceof Error ? err.message : String(err)}`,
        )
      } finally {
        setBusy((n) => n - 1)
      }
    }
    if (filed.length > 0) onChange([...attachments, ...filed])
  }

  const remove = async (attachment: Attachment) => {
    onChange(attachments.filter((a) => a.id !== attachment.id))
    await deleteAttachment(attachment)
  }

  const open = async (attachment: Attachment) => {
    try {
      window.open(await attachmentUrl(attachment), '_blank', 'noopener')
    } catch {
      setError(`${attachment.name} could not be opened.`)
    }
  }

  return (
    <section className="recform__group">
      <div className="att__head">
        <h4 className="recform__legend">Attachments</h4>
        <label className="att__pick">
          {busy > 0 ? `Filing ${busy}…` : 'Attach file'}
          <input
            type="file"
            multiple
            onChange={(e) => {
              void pick(e.target.files)
              e.target.value = ''
            }}
          />
        </label>
      </div>

      {attachments.length > 0 && (
        <div className="att__list">
          {attachments.map((a) => (
            <div className="att__row" key={a.id}>
              <button type="button" className="att__open" onClick={() => void open(a)}>
                <span className="att__name">{a.name}</span>
                <span className="att__meta">
                  {fmtFileSize(a.size)} · filed {formatDate(a.uploadedAt)}
                </span>
              </button>
              <button type="button" className="att__remove" onClick={() => void remove(a)}>
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      {attachments.length === 0 && busy === 0 && <p className="att__empty">{EMPTY[scope]}</p>}

      {error && <p className="att__error">{error}</p>}
    </section>
  )
}
